import React, { useCallback, useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { parse, stringify } from 'qs'

import { TableUrlPagination } from '../../../../ui'
import { useCombos } from '../../../combos'
import { useIPOrganismosContext } from '../../contexts'
import { ipOrganismosTableDataPreparation } from '../../logics'
import { IP_ORGANISMOS_TABLE_COLUMNS, DEFAULT_QUERY } from './ip-organismos-table.constants'

export const IPOrganismosTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useCombos([])
  const {
    data,
    pagination: { total_registros: totalEntries },
    loading,
    search,
    onClickTableRow,
    setSelectedTableRows,
    getTableData,
  } = useIPOrganismosContext()

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getTableData(searchParams)
    } else {
      navigate(`/ip-organismos?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getTableData, navigate])

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      IP_ORGANISMOS_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.ip.table.columns.${column.field}.title` }),
          size: column.width,
        }

        return {
          ...baseColumn,
          Cell: ({ cell, row }) => {
            const value = cell.getValue()
            if (column.render) {
              return column.render(value, row.original)
            }
            return value ?? '--'
          },
        }
      }),
    [formatMessage]
  )

  const processedData = useMemo(
    () => ipOrganismosTableDataPreparation({ data, combos, intl }),
    [data, combos, intl]
  )

  const tableOptions = useMemo(
    () => ({
      muiTableProps: {
        sx: {
          tableLayout: 'fixed',
        },
      },
      toolbar: false,
      selection: false,
      showSelectAllCheckbox: false,
      draggable: false,
    }),
    []
  )

  const handleRowClick = useCallback(
    (_, row) => {
      onClickTableRow(row.id)
    },
    [onClickTableRow]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
    />
  )
}
